import { COMMERCIAL_PLANS, COMMERCIAL_PLAN_LIMITS, formatCustomRolesLimit, formatPlanLimitSummary } from '@cevop/billing/catalog';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SIGNUP_URL } from '../config';
import { CheckGreen, ChevronDown } from '../components/icons';

type PlanKey = (typeof COMMERCIAL_PLANS)[number]['key'];

function majorUnits(amount: string): number {
  const [major] = amount.split('.');
  return Number.parseInt(major ?? '0', 10);
}

function formatNgn(amount: string) {
  if (amount === '0.00') return '₦0';
  return `₦${majorUnits(amount).toLocaleString('en-NG')}`;
}

const PLAN_COPY: Record<
  PlanKey,
  { tagline: string; limits: string; features: string[]; period: string }
> = {
  free: {
    tagline: 'Get started, no commitment.',
    limits: formatPlanLimitSummary('free'),
    period: 'forever',
    features: [
      'cevop Business + POS',
      '1 branch per business',
      'Up to 2 users',
      '1 custom role',
      'Up to 100 sales / month',
      '3 months of history',
    ],
  },
  starter: {
    tagline: 'For businesses ready for structured operations.',
    limits: formatPlanLimitSummary('starter'),
    period: '/month',
    features: [
      '1 branch per business',
      'Up to 4 users',
      'Up to 5 custom roles',
      'Unlimited sales',
      'Stock, Sales, Buying, Money, and POS',
      'Full history + CSV/XLSX/PDF export',
    ],
  },
  growth: {
    tagline: 'For businesses scaling operations.',
    limits: formatPlanLimitSummary('growth'),
    period: '/month',
    features: [
      'Up to 5 branches',
      'Unlimited users',
      'Up to 10 custom roles',
      'Unlimited sales',
      'Stock, Sales, Buying, Money, and POS',
      'Multi-location operations',
    ],
  },
  enterprise: {
    tagline: 'For larger or specialized requirements.',
    limits: formatPlanLimitSummary('enterprise'),
    period: '',
    features: [
      'Unlimited branches & users',
      'Multiple businesses under one account',
      'Consolidated owner view',
      'Unlimited custom roles',
      'Integration API',
      'Dedicated support',
    ],
  },
};

const comparison = [
  {
    group: 'Operations',
    rows: [
      {
        feature: 'Branches',
        free: String(COMMERCIAL_PLAN_LIMITS.free.maxBranches),
        starter: String(COMMERCIAL_PLAN_LIMITS.starter.maxBranches),
        growth: String(COMMERCIAL_PLAN_LIMITS.growth.maxBranches),
        enterprise: 'Unlimited',
      },
      {
        feature: 'Users',
        free: String(COMMERCIAL_PLAN_LIMITS.free.maxUsers),
        starter: String(COMMERCIAL_PLAN_LIMITS.starter.maxUsers),
        growth: 'Unlimited',
        enterprise: 'Unlimited',
      },
      {
        feature: 'Sales volume',
        free: `${COMMERCIAL_PLAN_LIMITS.free.maxCompletedSalesPerMonth} / mo`,
        starter: 'Unlimited',
        growth: 'Unlimited',
        enterprise: 'Unlimited',
      },
      { feature: 'Business workspace + POS', free: true, starter: true, growth: true, enterprise: true },
    ],
  },
  {
    group: 'Operations depth',
    rows: [
      { feature: 'Stock, Sales, Buying, Money', free: true, starter: true, growth: true, enterprise: true },
      {
        feature: 'CSV / XLSX / PDF export',
        free: COMMERCIAL_PLAN_LIMITS.free.allowsExport,
        starter: COMMERCIAL_PLAN_LIMITS.starter.allowsExport,
        growth: COMMERCIAL_PLAN_LIMITS.growth.allowsExport,
        enterprise: COMMERCIAL_PLAN_LIMITS.enterprise.allowsExport,
      },
      {
        feature: 'History window',
        free: `${COMMERCIAL_PLAN_LIMITS.free.historyMonths} months`,
        starter: 'Full',
        growth: 'Full',
        enterprise: 'Full',
      },
    ],
  },
  {
    group: 'Organisation',
    rows: [
      {
        feature: 'Multiple businesses',
        free: COMMERCIAL_PLAN_LIMITS.free.allowsMultipleBusinesses,
        starter: COMMERCIAL_PLAN_LIMITS.starter.allowsMultipleBusinesses,
        growth: COMMERCIAL_PLAN_LIMITS.growth.allowsMultipleBusinesses,
        enterprise: COMMERCIAL_PLAN_LIMITS.enterprise.allowsMultipleBusinesses,
      },
      {
        feature: 'Custom roles',
        free: formatCustomRolesLimit(COMMERCIAL_PLAN_LIMITS.free.maxCustomRoles),
        starter: formatCustomRolesLimit(COMMERCIAL_PLAN_LIMITS.starter.maxCustomRoles),
        growth: formatCustomRolesLimit(COMMERCIAL_PLAN_LIMITS.growth.maxCustomRoles),
        enterprise: formatCustomRolesLimit(COMMERCIAL_PLAN_LIMITS.enterprise.maxCustomRoles),
      },
      {
        feature: 'Integration API',
        free: COMMERCIAL_PLAN_LIMITS.free.allowsIntegrationApi,
        starter: COMMERCIAL_PLAN_LIMITS.starter.allowsIntegrationApi,
        growth: COMMERCIAL_PLAN_LIMITS.growth.allowsIntegrationApi,
        enterprise: COMMERCIAL_PLAN_LIMITS.enterprise.allowsIntegrationApi,
      },
      {
        feature: 'Dedicated support',
        free: COMMERCIAL_PLAN_LIMITS.free.dedicatedSupport,
        starter: COMMERCIAL_PLAN_LIMITS.starter.dedicatedSupport,
        growth: COMMERCIAL_PLAN_LIMITS.growth.dedicatedSupport,
        enterprise: COMMERCIAL_PLAN_LIMITS.enterprise.dedicatedSupport,
      },
    ],
  },
];

const faqs = [
  {
    q: 'Can I start for free?',
    a: 'Yes. The Free plan does not require a card. Limits are listed on this page and enforced in the product.',
  },
  {
    q: 'How do I sign up?',
    a: 'Start free opens the Business app signup. Sign in is for existing accounts.',
  },
  {
    q: 'Is checkout on this website?',
    a: 'Paid billing is not completed on the marketing site. Plan amounts shown are the canonical NGN monthly prices. Annual billing is not listed as a live price yet.',
  },
  {
    q: 'Can I use cevop for multiple locations?',
    a: 'Growth includes up to five branches. Enterprise is arranged with sales for unlimited branches and multiple businesses.',
  },
  {
    q: 'What payment methods can my customers use?',
    a: 'Cashiers can record cash, card, or transfer on POS. cevop stores the method and amount you enter — not card numbers.',
  },
];

function Check({ yes }: { yes: boolean | string }) {
  if (typeof yes === 'string') return <span className="font-mono-data text-[0.75rem] text-ink">{yes}</span>;
  return yes ? (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-brand" aria-hidden>
      <path d="M2.5 8L6.5 12L13.5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <div className="mx-auto h-px w-3 bg-line-strong" />
  );
}

export function PricingPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [compOpen, setCompOpen] = useState<string | null>(null);

  return (
    <>
      <section className="px-6 pt-28 pb-14 text-center lg:px-10 lg:pt-36 lg:pb-16">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand/15 bg-brand-light px-3 py-1.5 text-[0.72rem] font-bold tracking-widest text-brand uppercase">
            Pricing
          </div>
          <h1 className="font-display mb-4 text-[2.75rem] leading-[1.06] font-extrabold text-ink lg:text-[3.625rem]">
            Clear pricing. No guessing.
          </h1>
          <p className="mb-8 text-[1.0625rem] text-ink-3">
            Choose the plan that fits your business. Amounts are NGN per business, per month.
          </p>
        </div>
      </section>

      <section className="px-6 pb-20 lg:px-10">
        <div className="mx-auto max-w-[1280px]">
          <div className="mk-stagger grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {COMMERCIAL_PLANS.map((catalogPlan) => {
              const id = catalogPlan.key;
              const copy = PLAN_COPY[id];
              const featured = id === 'starter';
              const monthly = catalogPlan.prices.find((price) => price.interval === 'MONTH' && price.currencyCode === 'NGN');
              const priceLabel = catalogPlan.contactSales ? 'Contact us' : formatNgn(monthly?.amount ?? '0.00');
              return (
                <div
                  key={id}
                  className={`mk-lift relative flex flex-col rounded-2xl p-6 ${
                    featured ? 'bg-inverse shadow-xl ring-1 ring-brand/40' : 'border border-line bg-panel shadow-sm'
                  }`}
                >
                  {featured && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-brand px-3 py-1 text-[0.65rem] font-bold text-brand-on shadow-sm">
                      Most popular
                    </div>
                  )}

                  <div
                    className="mb-4 border-b pb-4"
                    style={{ borderColor: featured ? 'color-mix(in srgb, var(--mk-inverse-fg) 8%, transparent)' : 'var(--line)' }}
                  >
                    <h3 className={`font-display mb-1 text-[1.05rem] font-bold ${featured ? 'text-inverse-fg' : 'text-ink'}`}>
                      {catalogPlan.name}
                    </h3>
                    <div className={`text-[0.75rem] leading-snug ${featured ? 'text-inverse-fg/40' : 'text-ink-3'}`}>
                      {copy.tagline}
                    </div>
                  </div>

                  <div className="mb-5">
                    <div className={`font-display text-[2rem] leading-none font-extrabold ${featured ? 'text-inverse-fg' : 'text-ink'}`}>
                      {priceLabel}
                      {!catalogPlan.contactSales && monthly && monthly.amount !== '0.00' && (
                        <span className={`text-[0.72rem] font-medium ${featured ? 'text-inverse-fg/35' : 'text-ink-3'}`}> /mo</span>
                      )}
                    </div>
                    {copy.period && (
                      <div className={`mt-1 text-[0.72rem] ${featured ? 'text-inverse-fg/35' : 'text-ink-3'}`}>{copy.period}</div>
                    )}
                    <div className={`mt-2 text-[0.65rem] font-medium ${featured ? 'text-inverse-fg/25' : 'text-ink-3'}`}>
                      {copy.limits}
                    </div>
                  </div>

                  <div className="mb-6 flex-1 space-y-2">
                    {copy.features.map((f) => (
                      <div key={f} className="flex items-start gap-2.5">
                        <CheckGreen />
                        <span className={`text-[0.78rem] leading-snug ${featured ? 'text-inverse-fg/65' : 'text-ink-2'}`}>{f}</span>
                      </div>
                    ))}
                  </div>

                  {catalogPlan.contactSales ? (
                    <Link
                      to="/contact"
                      className="rounded-xl border border-line-strong py-3 text-center text-[0.875rem] font-semibold text-ink transition-colors hover:bg-surface-2"
                    >
                      Contact sales
                    </Link>
                  ) : (
                    <a
                      href={SIGNUP_URL}
                      className={`rounded-xl py-3 text-center text-[0.875rem] font-semibold transition-colors ${
                        featured
                          ? 'bg-brand text-brand-on hover:bg-brand-hover'
                          : 'border border-line-strong text-ink hover:bg-surface-2'
                      }`}
                    >
                      {id === 'free' ? 'Start free' : 'Get started'}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-8 max-w-[65ch] text-[0.85rem] text-ink-3">
            Billed per business, not per person. Annual billing is not a live catalog price yet. USD approximations are not
            listed here because they are not Billing prices.
          </p>
        </div>
      </section>

      <section className="border-y border-line bg-surface-2/60 px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-10 text-center">
            <h2 className="font-display mb-3 text-[2rem] leading-[1.1] font-extrabold text-ink lg:text-[2.375rem]">
              Compare plans
            </h2>
            <p className="text-[0.95rem] text-ink-3">See what each plan includes in the current catalog.</p>
          </div>

          <div className="hidden overflow-hidden rounded-2xl border border-line bg-panel shadow-sm lg:block">
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] border-b border-line">
              <div className="p-4 text-[0.72rem] font-bold tracking-widest text-ink-3 uppercase">Feature</div>
              {['Free', 'Starter', 'Growth', 'Enterprise'].map((p, i) => (
                <div key={p} className={`p-4 text-center ${i === 1 ? 'bg-inverse' : ''}`}>
                  <div className={`font-display text-[0.9rem] font-bold ${i === 1 ? 'text-inverse-fg' : 'text-ink'}`}>{p}</div>
                </div>
              ))}
            </div>

            {comparison.map((group) => (
              <div key={group.group}>
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] border-b border-line bg-surface-2">
                  <div className="col-span-5 px-4 py-2 text-[0.7rem] font-bold tracking-widest text-ink-3 uppercase">
                    {group.group}
                  </div>
                </div>
                {group.rows.map((row, i) => (
                  <div
                    key={row.feature}
                    className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr] border-b border-surface-2 last:border-0 ${i % 2 === 0 ? '' : 'bg-surface'}`}
                  >
                    <div className="px-4 py-3 text-[0.82rem] text-ink-2">{row.feature}</div>
                    {[row.free, row.starter, row.growth, row.enterprise].map((val, j) => (
                      <div key={j} className={`flex items-center justify-center px-4 py-3 ${j === 1 ? 'bg-inverse/20' : ''}`}>
                        <Check yes={val as boolean | string} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="space-y-3 lg:hidden">
            {comparison.map((group) => (
              <div key={group.group} className="overflow-hidden rounded-2xl border border-line bg-panel">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-5 py-4"
                  onClick={() => setCompOpen(compOpen === group.group ? null : group.group)}
                >
                  <span className="font-display text-[0.9rem] font-bold text-ink">{group.group}</span>
                </button>
                {compOpen === group.group && (
                  <div className="border-t border-line">
                    {group.rows.map((row) => (
                      <div key={row.feature} className="border-t border-surface-2 px-5 py-3">
                        <div className="mb-2 text-[0.78rem] text-ink-2">{row.feature}</div>
                        <div className="grid grid-cols-4 gap-2 text-center text-[0.65rem]">
                          {(['free', 'starter', 'growth', 'enterprise'] as const).map((k) => (
                            <div key={k}>
                              <div className="mb-1 font-bold text-ink-3 uppercase">{k.slice(0, 3)}</div>
                              <div className="flex justify-center">
                                <Check yes={row[k] as boolean | string} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[760px]">
          <div className="mb-10 text-center">
            <h2 className="font-display text-[2rem] leading-[1.1] font-extrabold text-ink lg:text-[2.375rem]">
              Pricing questions
            </h2>
          </div>
          <div className="space-y-2">
            {faqs.map((item, i) => (
              <div key={item.q} className="overflow-hidden rounded-xl border border-line bg-panel shadow-sm">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                >
                  <span className="text-[0.875rem] font-semibold text-ink">{item.q}</span>
                  <ChevronDown className={`shrink-0 text-ink-3 transition-transform duration-200 ${faqOpen === i ? 'rotate-180' : ''}`} />
                </button>
                {faqOpen === i && (
                  <div className="mk-faq-in border-t border-surface-2 px-5 pb-4 text-[0.85rem] leading-[1.7] text-ink-3">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-inverse px-6 py-16 lg:px-10">
        <div className="mx-auto max-w-[700px] text-center">
          <h2 className="font-display mb-4 text-[2rem] text-inverse-fg lg:text-[2.375rem]">Need something specific?</h2>
          <p className="mb-7 text-[1rem] text-inverse-fg/50">
            Enterprise is contact-sales. Talk to us about branches, multiple businesses, and support.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl bg-inverse-fg px-8 py-3.5 text-[0.9375rem] font-semibold text-inverse transition-colors hover:bg-inverse-fg/90"
          >
            Contact sales
          </Link>
        </div>
      </section>
    </>
  );
}
